// Input - keyboard, mouse, and touch state tracking

// Thresholds for distinguishing a "tap" (attack) from a "drag" (move).
const TAP_MAX_DURATION_MS = 220;
const TAP_MAX_MOVE_PX = 20;
// Virtual joystick: dead zone around anchor before movement starts; max
// thumb travel for visual rendering. Direction is normalized regardless
// of distance once outside the dead zone.
const JOYSTICK_DEADZONE_PX = 14;
const JOYSTICK_MAX_RANGE_PX = 60;

export class Input {
  constructor(canvas) {
    this.keys = {};
    this.keysPressed = {};  // true only on the frame the key was first pressed
    this.mouse = { x: 0, y: 0, down: false, rightDown: false, clicked: false };
    this.touch = { active: false, x: 0, y: 0 };
    this._canvas = canvas;

    // Player screen position, updated each frame by the game so getMovement()
    // can resolve touch direction without coupling input to player/camera.
    this._playerScreenX = 0;
    this._playerScreenY = 0;

    // Four-zone touch tracking:
    //   - Dash touch: started inside dash button rect. One-shot trigger
    //     (`_touchDashPressed`) consumed by getter, cleared in endFrame.
    //   - Super touch: started inside super button rect. One-shot like dash.
    //   - Attack touch: started inside attack button rect. Held =
    //     continuous auto-aim attack.
    //   - Move touch: anywhere else. Drives movement (held) and short
    //     tap (sets mouse.clicked, e.g. for shop / UI).
    // Multi-touch beyond these slots is dropped.
    this._moveTouchId = null;
    this._attackTouchId = null;
    this._dashTouchId = null;
    this._touchDashPressed = false;
    // Super button: one-shot like dash.
    this._superTouchId = null;
    this._touchSuperPressed = false;
    this._touchStartX = 0;
    this._touchStartY = 0;
    this._touchStartTime = 0;

    // Auto-attack lock gesture: drag off the attack button and release on
    // the lock target that appears left of it. Tracked here (rather than as
    // a second button) so the whole thing is one continuous thumb motion.
    this._attackTouchX = 0;
    this._attackTouchY = 0;
    this._autoAttackToggled = false;

    // Optional rects (canvas coords) for on-screen control buttons.
    // UI updates these each frame; when null, every touch goes to move.
    this._attackBtnRect = null;
    this._dashBtnRect = null;
    this._superBtnRect = null;
    this._autoLockRect = null;

    // Caps Lock is a latching key and browsers disagree about how it
    // reports. Windows/Linux fire keydown *and* keyup for one physical
    // press; macOS fires keydown when the lamp turns on and only keyup
    // when it turns off. Edge-triggering on either event alone therefore
    // either double-fires or misses every second toggle. Tracking the
    // modifier state and emitting a press only when it flips handles all
    // three.
    //
    // The lamp's state at page load is unknowable, and it can also change
    // while the tab is unfocused, so `_capsLockOn` starts unknown: the
    // first Caps Lock event we see is by definition a press, whatever
    // state it reports. Non-CapsLock keys resync the tracked value
    // silently, so a change made while we weren't listening can't swallow
    // the player's next real press.
    this._capsLockOn = null;
    this._syncCapsLock = (e) => {
      if (!e.getModifierState) return;
      const on = e.getModifierState('CapsLock');
      if (this._capsLockOn === null || on !== this._capsLockOn) {
        this._capsLockOn = on;
        this.keysPressed.CapsLock = true;
      }
    };
    this._observeCapsLock = (e) => {
      if (!e.getModifierState) return;
      this._capsLockOn = e.getModifierState('CapsLock');
    };

    this._onKeyDown = (e) => {
      // Typing a nickname (js/name-entry.js) isn't steering the robber.
      if (isTypingTarget(e)) return;
      if (e.code === 'CapsLock') {
        this._syncCapsLock(e);
        this.keys[e.code] = true;
        return;
      }
      this._observeCapsLock(e);
      if (!this.keys[e.code]) {
        this.keysPressed[e.code] = true;
      }
      this.keys[e.code] = true;
      // Debug: log telemetry/export hotkeys so we can confirm the event
      // actually reaches the window listener (e.g. rule out focus issues).
      if (e.code === 'KeyL' || e.code === 'KeyU') {
        console.log(`[input] keydown ${e.code} shift=${e.shiftKey} target=${e.target && e.target.tagName}`);
      }
      // Prevent default for game keys
      if (['Space', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.code)) {
        e.preventDefault();
      }
    };

    this._onKeyUp = (e) => {
      if (e.code === 'CapsLock') this._syncCapsLock(e);
      this.keys[e.code] = false;
    };

    this._onMouseMove = (e) => {
      const pt = this._toCanvas(e.clientX, e.clientY);
      this.mouse.x = pt.x;
      this.mouse.y = pt.y;
    };

    this._onMouseDown = (e) => {
      if (e.button === 0) {
        this.mouse.down = true;
        this.mouse.clicked = true;
      } else if (e.button === 2) {
        this.mouse.rightDown = true;
        // Right-click is hold-to-move only; swallow the browser menu.
        e.preventDefault();
      }
    };

    this._onMouseUp = (e) => {
      if (e.button === 0) {
        this.mouse.down = false;
      } else if (e.button === 2) {
        this.mouse.rightDown = false;
      }
    };

    this._onContextMenu = (e) => {
      // Block the native context menu so RMB can be used for movement.
      e.preventDefault();
    };

    this._onTouchStart = (e) => {
      for (const t of e.changedTouches) {
        const pt = this._toCanvas(t.clientX, t.clientY);
        // Dash hit-test runs first because the dash button sits above the
        // attack button — overlapping rects must resolve to dash.
        if (this._isInDashRect(pt.x, pt.y)) {
          if (this._dashTouchId === null) {
            this._dashTouchId = t.identifier;
            this._touchDashPressed = true;
          }
          continue;
        }
        if (this._isInSuperRect(pt.x, pt.y)) {
          if (this._superTouchId === null) {
            this._superTouchId = t.identifier;
            this._touchSuperPressed = true;
          }
          continue;
        }
        if (this._isInAttackRect(pt.x, pt.y)) {
          if (this._attackTouchId === null) {
            this._attackTouchId = t.identifier;
            this._attackTouchX = pt.x;
            this._attackTouchY = pt.y;
          }
          continue;
        }
        if (this._moveTouchId !== null) continue;
        this._moveTouchId = t.identifier;
        this.touch.active = true;
        this.touch.x = pt.x;
        this.touch.y = pt.y;
        this._touchStartX = pt.x;
        this._touchStartY = pt.y;
        this._touchStartTime = performance.now();
        // Keep mouse position in sync so UI hover/hit-tests work.
        this.mouse.x = pt.x;
        this.mouse.y = pt.y;
      }
      e.preventDefault();
    };

    this._onTouchMove = (e) => {
      for (const t of e.changedTouches) {
        if (t.identifier === this._moveTouchId) {
          const pt = this._toCanvas(t.clientX, t.clientY);
          this.touch.x = pt.x;
          this.touch.y = pt.y;
          this.mouse.x = pt.x;
          this.mouse.y = pt.y;
        }
        // The attack touch keeps firing wherever it goes, but its position
        // matters for the lock gesture (and for drawing the drag hint).
        if (t.identifier === this._attackTouchId) {
          const pt = this._toCanvas(t.clientX, t.clientY);
          this._attackTouchX = pt.x;
          this._attackTouchY = pt.y;
        }
      }
      e.preventDefault();
    };

    this._onTouchEnd = (e) => {
      for (const t of e.changedTouches) {
        if (t.identifier === this._moveTouchId) {
          const duration = performance.now() - this._touchStartTime;
          const dx = this.touch.x - this._touchStartX;
          const dy = this.touch.y - this._touchStartY;
          const moved = Math.sqrt(dx * dx + dy * dy);
          // Short, near-stationary release = tap: route through mouse.clicked so
          // existing attack / UI click code paths fire without special cases.
          if (duration < TAP_MAX_DURATION_MS && moved < TAP_MAX_MOVE_PX) {
            this.mouse.clicked = true;
          }
          this.touch.active = false;
          this._moveTouchId = null;
        } else if (t.identifier === this._attackTouchId) {
          // Released on the lock target = toggle auto-attack. Consumed as
          // a one-shot edge trigger, like the dash press.
          const pt = this._toCanvas(t.clientX, t.clientY);
          if (this._isInAutoLockRect(pt.x, pt.y)) {
            this._autoAttackToggled = true;
          }
          this._attackTouchId = null;
        } else if (t.identifier === this._dashTouchId) {
          this._dashTouchId = null;
        } else if (t.identifier === this._superTouchId) {
          this._superTouchId = null;
        }
      }
      e.preventDefault();
    };

    this._onTouchCancel = (e) => {
      for (const t of e.changedTouches) {
        if (t.identifier === this._moveTouchId) {
          this.touch.active = false;
          this._moveTouchId = null;
        } else if (t.identifier === this._attackTouchId) {
          this._attackTouchId = null;
        } else if (t.identifier === this._dashTouchId) {
          this._dashTouchId = null;
        } else if (t.identifier === this._superTouchId) {
          this._superTouchId = null;
        }
      }
    };

    window.addEventListener('keydown', this._onKeyDown);
    window.addEventListener('keyup', this._onKeyUp);
    canvas.addEventListener('mousemove', this._onMouseMove);
    canvas.addEventListener('mousedown', this._onMouseDown);
    canvas.addEventListener('mouseup', this._onMouseUp);
    canvas.addEventListener('contextmenu', this._onContextMenu);
    canvas.addEventListener('touchstart', this._onTouchStart, { passive: false });
    canvas.addEventListener('touchmove', this._onTouchMove, { passive: false });
    canvas.addEventListener('touchend', this._onTouchEnd, { passive: false });
    canvas.addEventListener('touchcancel', this._onTouchCancel, { passive: false });
  }

  _toCanvas(clientX, clientY) {
    const canvas = this._canvas;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    return {
      x: (clientX - rect.left) * scaleX,
      y: (clientY - rect.top) * scaleY,
    };
  }

  // Call at end of each frame to reset per-frame state
  endFrame() {
    this.keysPressed = {};
    this.mouse.clicked = false;
    this._touchDashPressed = false;
    this._touchSuperPressed = false;
    this._autoAttackToggled = false;
  }

  isDown(code) {
    return !!this.keys[code];
  }

  wasPressed(code) {
    return !!this.keysPressed[code];
  }

  // Game sets this each frame so getMovement() can resolve touch direction.
  setPlayerScreen(x, y) {
    this._playerScreenX = x;
    this._playerScreenY = y;
  }

  // UI tells Input where the on-screen attack button is so touchstart can
  // route into the attack slot vs the move slot. Pass null to clear.
  setAttackButtonRect(rect) {
    this._attackBtnRect = rect || null;
  }

  setDashButtonRect(rect) {
    this._dashBtnRect = rect || null;
  }

  setSuperButtonRect(rect) {
    this._superBtnRect = rect || null;
  }

  // Target the attack touch is dragged onto to toggle auto-attack. Only
  // meaningful while an attack touch is held; UI passes null otherwise.
  setAutoLockRect(rect) {
    this._autoLockRect = rect || null;
  }

  _inRect(rect, x, y) {
    if (!rect) return false;
    return x >= rect.x && x <= rect.x + rect.w && y >= rect.y && y <= rect.y + rect.h;
  }

  _isInAttackRect(x, y) {
    return this._inRect(this._attackBtnRect, x, y);
  }

  _isInDashRect(x, y) {
    return this._inRect(this._dashBtnRect, x, y);
  }

  _isInSuperRect(x, y) {
    return this._inRect(this._superBtnRect, x, y);
  }

  _isInAutoLockRect(x, y) {
    return this._inRect(this._autoLockRect, x, y);
  }

  // True while a touch is held inside the attack button (auto-aim attack).
  wantsTouchAttack() {
    return this._attackTouchId !== null;
  }

  // One-shot edge trigger: true on the frame the dash button was first
  // touched. Cleared in endFrame() like other per-frame state.
  wantsTouchDash() {
    return this._touchDashPressed;
  }

  // One-shot edge trigger: true on the frame the super button was touched.
  wantsTouchSuper() {
    return this._touchSuperPressed;
  }

  // One-shot edge trigger: the attack touch was released on the lock target.
  wantsAutoAttackToggle() {
    return this._autoAttackToggled;
  }

  // Where the held attack touch currently is, and whether it's over the
  // lock target — UI uses this to draw the drag hint and highlight.
  // Returns null when no attack touch is held.
  getAttackDragState() {
    if (this._attackTouchId === null) return null;
    return {
      x: this._attackTouchX,
      y: this._attackTouchY,
      overLock: this._isInAutoLockRect(this._attackTouchX, this._attackTouchY),
    };
  }

  // Movement vector from WASD / arrow keys, normalized.
  // Falls back to touch: direction from player screen position to held touch.
  getMovement() {
    let x = 0, y = 0;
    if (this.isDown('KeyW') || this.isDown('ArrowUp')) y -= 1;
    if (this.isDown('KeyS') || this.isDown('ArrowDown')) y += 1;
    if (this.isDown('KeyA') || this.isDown('ArrowLeft')) x -= 1;
    if (this.isDown('KeyD') || this.isDown('ArrowRight')) x += 1;
    const len = Math.sqrt(x * x + y * y);
    if (len > 0) {
      return { x: x / len, y: y / len };
    }
    // Touch movement: virtual joystick anchored at first touch position.
    // Direction = (current - anchor) normalized, once outside deadzone.
    if (this.touch.active) {
      const dx = this.touch.x - this._touchStartX;
      const dy = this.touch.y - this._touchStartY;
      const d = Math.sqrt(dx * dx + dy * dy);
      if (d > JOYSTICK_DEADZONE_PX) {
        return { x: dx / d, y: dy / d };
      }
    }
    // Held left mouse button = walk toward cursor (player-relative). Kept
    // unchanged — desktop convention, no joystick anchor.
    if (this.mouse.down) {
      const dx = this.mouse.x - this._playerScreenX;
      const dy = this.mouse.y - this._playerScreenY;
      const d = Math.sqrt(dx * dx + dy * dy);
      if (d > JOYSTICK_DEADZONE_PX) {
        return { x: dx / d, y: dy / d };
      }
    }
    return { x: 0, y: 0 };
  }

  // Snapshot of joystick state for UI rendering. Returns null when no
  // active move-touch (joystick is hidden in that case).
  getJoystickState() {
    if (!this.touch.active) return null;
    const dx = this.touch.x - this._touchStartX;
    const dy = this.touch.y - this._touchStartY;
    const d = Math.sqrt(dx * dx + dy * dy);
    const max = JOYSTICK_MAX_RANGE_PX;
    const clampScale = d > max ? max / d : 1;
    return {
      ox: this._touchStartX,
      oy: this._touchStartY,
      tx: this._touchStartX + dx * clampScale,
      ty: this._touchStartY + dy * clampScale,
      maxRange: max,
      deadzone: JOYSTICK_DEADZONE_PX,
      active: d > JOYSTICK_DEADZONE_PX,
    };
  }

  wantsAttack() {
    // Space held = auto-repeat attacks (auto-aims at nearest enemy).
    // Right mouse held = aimed attacks toward cursor.
    // Touch tap = single attack (routed through mouse.clicked).
    // On-screen attack button held = continuous auto-aim attack.
    return this.isDown('Space') || this.mouse.rightDown || this.mouse.clicked
      || this.wantsTouchAttack();
  }

  destroy() {
    window.removeEventListener('keydown', this._onKeyDown);
    window.removeEventListener('keyup', this._onKeyUp);
    this._canvas.removeEventListener('mousemove', this._onMouseMove);
    this._canvas.removeEventListener('mousedown', this._onMouseDown);
    this._canvas.removeEventListener('mouseup', this._onMouseUp);
    this._canvas.removeEventListener('contextmenu', this._onContextMenu);
    this._canvas.removeEventListener('touchstart', this._onTouchStart);
    this._canvas.removeEventListener('touchmove', this._onTouchMove);
    this._canvas.removeEventListener('touchend', this._onTouchEnd);
    this._canvas.removeEventListener('touchcancel', this._onTouchCancel);
  }
}

// True when a key event comes from a text field, so game hotkeys leave it be.
export function isTypingTarget(e) {
  const el = e && e.target;
  return !!el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || !!el.isContentEditable);
}
